import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { ApiError } from '../../lib/api';
import {
  listPublicCorrections,
  type PublicCorrectionCard,
  type PublicCorrectionRegistryFilters,
  type PublicCorrectionRegistryOrdering,
} from '../../lib/manualCorrectionsApi';

const submittedAtFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Moscow',
});

const statusCopy: Record<
  PublicCorrectionCard['status'],
  { label: string; status: string; className: string }
> = {
  PENDING: {
    label: 'В обработке',
    status: 'pending',
    className: 'border-yellow-500/60 bg-yellow-400/15 text-foreground',
  },
  APPROVED_AWAITING_RECALC: {
    label: 'Одобрено',
    status: 'approved',
    className: 'border-success/60 bg-success/15 text-foreground',
  },
  REJECTED: {
    label: 'Отказано',
    status: 'rejected',
    className: 'border-destructive/60 bg-destructive/15 text-foreground',
  },
};

const formatSubmittedAt = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : submittedAtFormatter.format(date);
};

const formatParticipant = (name: string, squad: string): string =>
  squad ? `[${squad}]${name}` : name;

const columnLabels: Record<string, string> = {
  requestId: 'ID заявки',
  createdAt: 'Дата и время подачи',
  killer: 'Кто',
  victim: 'Кого',
  weapon: 'Оружие',
  distance: 'Дистанция',
  replay: 'Ссылка на реплей',
  status: 'Статус',
  note: 'Примечание',
};

function SortIndicator({ direction }: { direction: false | 'asc' | 'desc' }): JSX.Element {
  return (
    <svg aria-hidden="true" viewBox="0 0 12 14" className="h-3.5 w-3 shrink-0" fill="currentColor">
      <path
        d={direction === 'desc' ? 'M2 4h8L6 10z' : direction === 'asc' ? 'M2 10h8L6 4z' : 'M2 5h8L6 1zM2 9h8l-4 4z'}
      />
    </svg>
  );
}

export function ManualCorrectionRegistry(): JSX.Element {
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterValues, setFilterValues] = useState<PublicCorrectionRegistryFilters>({});
  const [debouncedFilters, setDebouncedFilters] = useState<PublicCorrectionRegistryFilters>({});
  const [activeNote, setActiveNote] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const noteTriggerRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedFilters(filterValues);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [filterValues]);

  const ordering = sorting[0]
    ? `${sorting[0].desc ? '-' : ''}${sorting[0].id}` as
        | PublicCorrectionRegistryOrdering
        | `-${PublicCorrectionRegistryOrdering}`
    : undefined;
  const query = useQuery({
    queryKey: ['manual-corrections', page, ordering, debouncedFilters],
    placeholderData: (previousData) => previousData,
    queryFn: () => listPublicCorrections({
      page,
      ...(ordering ? { ordering } : {}),
      ...(Object.keys(debouncedFilters).length ? { filters: debouncedFilters } : {}),
    }),
  });

  const finishClosingNote = () => {
    setActiveNote(null);
    noteTriggerRef.current?.focus();
  };

  const openNote = (note: string, trigger: HTMLButtonElement) => {
    setActiveNote(note);
    noteTriggerRef.current = trigger;
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  };

  const closeNote = () => {
    const dialog = dialogRef.current;
    if (dialog && typeof dialog.close === 'function') {
      dialog.close();
      return;
    }
    dialog?.removeAttribute('open');
    finishClosingNote();
  };

  const columns = useMemo<ColumnDef<PublicCorrectionCard>[]>(
    () => [
      {
        id: 'requestId',
        accessorKey: 'request_id',
        header: 'ID Заявки',
      },
      {
        id: 'createdAt',
        accessorKey: 'created_at',
        header: 'Дата и время подачи',
        cell: ({ row }) => formatSubmittedAt(row.original.created_at),
      },
      {
        id: 'killer',
        accessorFn: (row) => formatParticipant(row.claimed_killer_name, row.claimed_killer_squad),
        enableColumnFilter: true,
        enableSorting: true,
        header: 'Кто',
        cell: ({ row }) =>
          formatParticipant(
            row.original.claimed_killer_name,
            row.original.claimed_killer_squad
          ),
      },
      {
        id: 'victim',
        accessorFn: (row) => formatParticipant(row.claimed_victim_name, row.claimed_victim_squad),
        enableColumnFilter: true,
        enableSorting: true,
        header: 'Кого',
        cell: ({ row }) =>
          formatParticipant(
            row.original.claimed_victim_name,
            row.original.claimed_victim_squad
          ),
      },
      {
        id: 'weapon',
        accessorKey: 'claimed_weapon_or_method',
        header: 'Оружие',
        cell: ({ row }) => row.original.claimed_weapon_or_method || '—',
      },
      {
        id: 'distance',
        accessorKey: 'claimed_distance',
        header: 'Дистанция',
        cell: ({ row }) =>
          row.original.claimed_distance === null
            ? '—'
            : `${row.original.claimed_distance} м`,
      },
      {
        id: 'replay',
        accessorKey: 'replay_url',
        header: 'Ссылка на реплей',
        cell: ({ row }) => (
          <a
            className="font-semibold text-primary underline decoration-primary/50 underline-offset-4 hover:decoration-primary"
            href={row.original.replay_url}
            target="_blank"
            rel="noreferrer"
          >
            ссылка
          </a>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Статус',
        cell: ({ row }) => {
          const status = statusCopy[row.original.status];
          return (
            <span
              data-status={status.status}
              className={`inline-flex whitespace-nowrap rounded-md border px-2.5 py-1 text-xs font-semibold ${status.className}`}
            >
              {status.label}
            </span>
          );
        },
      },
      {
        id: 'note',
        accessorKey: 'public_rejection_reason',
        header: 'Примечание',
        cell: ({ row }) => {
          const note = row.original.public_rejection_reason?.trim();
          return note ? (
            <button
              type="button"
              className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold transition hover:border-primary hover:text-primary"
              aria-label="Показать примечание"
              onClick={(event) => openNote(note, event.currentTarget)}
            >
              Показать
            </button>
          ) : (
            '—'
          );
        },
      },
    ],
    []
  );

  const rows = query.data?.results ?? [];
  const table = useReactTable({
    data: rows,
    columns,
    defaultColumn: { sortDescFirst: false },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange: (updater) => {
      setSorting(updater);
      setPage(1);
    },
    state: { sorting },
  });

  const setColumnFilter = (columnId: string, value: string) => {
    setFilterValues((current) => {
      if (value) return { ...current, [columnId]: value };
      const next = { ...current };
      delete next[columnId as keyof PublicCorrectionRegistryFilters];
      return next;
    });
  };

  const renderFilter = (columnId: string, value: unknown) => {
    const label = columnLabels[columnId];
    const commonClassName = 'mt-2 h-8 w-full min-w-[110px] rounded-md border border-border bg-background px-2 text-xs font-normal normal-case tracking-normal text-foreground';
    if (columnId === 'status') {
      return (
        <select
          aria-label={`Поиск: ${label}`}
          className={commonClassName}
          value={String(value ?? '')}
          onChange={(event) => setColumnFilter(columnId, event.target.value)}
        >
          <option value="">Все</option>
          <option value="PENDING">В обработке</option>
          <option value="APPROVED_AWAITING_RECALC">Одобрено</option>
          <option value="REJECTED">Отказано</option>
        </select>
      );
    }
    const type = columnId === 'createdAt' ? 'date' : ['requestId', 'distance'].includes(columnId) ? 'number' : 'search';
    return (
      <input
        aria-label={`Поиск: ${label}`}
        className={commonClassName}
        type={type}
        min={columnId === 'requestId' ? 1 : type === 'number' ? 0 : undefined}
        value={String(value ?? '')}
        onChange={(event) => setColumnFilter(columnId, event.target.value)}
      />
    );
  };

  return (
    <main className="mx-auto w-full max-w-screen-2xl space-y-6 px-4 py-6 sm:px-6">
      <header className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Учёт фрагов
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">Реестр заявок</h1>
        <p className="mt-3 text-muted-foreground">
          Публичные сведения о ходе рассмотрения заявок. Доказательства и данные проверяющих скрыты.
        </p>
      </header>

      {query.isLoading ? (
        <section aria-busy="true" aria-label="Загрузка реестра" className="card h-40 animate-pulse" />
      ) : query.isError ? (
        <section role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
          <p>
            {query.error instanceof ApiError
              ? query.error.message
              : 'Не удалось загрузить реестр.'}
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="mt-3 rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold hover:border-primary"
          >
            Повторить
          </button>
        </section>
      ) : (
        <section className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table aria-label="Реестр заявок" className="min-w-[1320px] w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} className="px-3 py-3 text-left align-middle font-semibold">
                        {header.isPlaceholder ? null : (
                          <>
                            <button
                              type="button"
                              aria-label={`Сортировать: ${columnLabels[header.column.id]}`}
                              className="flex items-center gap-1.5 whitespace-nowrap hover:text-foreground"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <SortIndicator direction={header.column.getIsSorted()} />
                            </button>
                            {renderFilter(
                              header.column.id,
                              filterValues[header.column.id as keyof PublicCorrectionRegistryFilters]
                            )}
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.original.public_id} className="border-t border-border align-top">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-3 py-3 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-10 text-center text-muted-foreground">
                      Заявок пока нет.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!query.isLoading && !query.isError ? (
        <nav aria-label="Страницы реестра" className="flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={!query.data?.previous || query.isFetching}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Назад
          </button>
          <span className="text-center text-sm text-muted-foreground">
            Страница {page} · всего {query.data?.count ?? 0}
          </span>
          <button
            type="button"
            disabled={!query.data?.next || query.isFetching}
            onClick={() => setPage((current) => current + 1)}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Вперёд
          </button>
        </nav>
      ) : null}

      <dialog
        ref={dialogRef}
        aria-labelledby="moderator-note-title"
        onClose={finishClosingNote}
        onClick={(event) => {
          if (event.target === event.currentTarget) closeNote();
        }}
        className="fixed inset-x-0 bottom-0 top-auto m-0 max-h-[85vh] w-full max-w-none rounded-t-xl border border-border bg-card p-0 text-foreground shadow-2xl backdrop:bg-black/60 sm:inset-0 sm:m-auto sm:w-[calc(100%-2rem)] sm:max-w-lg sm:rounded-xl"
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <h2 id="moderator-note-title" className="text-xl font-semibold">
              Примечание модератора
            </h2>
            <button
              type="button"
              aria-label="Закрыть"
              onClick={closeNote}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition hover:text-foreground"
            >
              ✕
            </button>
          </div>
          <p className="mt-4 max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words text-sm leading-6">
            {activeNote}
          </p>
        </div>
      </dialog>
    </main>
  );
}
